
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { 
  Save,
  RefreshCcw,
  Upload
} from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";

import { SubsidySettings, getSubsidySettings, saveSubsidySettings } from "@/lib/db/subsidyDb";

const SubsidySettingsTab: React.FC = () => {
  const [settings, setSettings] = useState<SubsidySettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("eligibility");
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();
  
  const { register, handleSubmit, setValue, reset, watch, formState: { errors, isDirty } } = useForm();
  
  useEffect(() => {
    const loadSettings = async () => {
      setIsLoading(true);
      try {
        const data = await getSubsidySettings();
        setSettings(data);
        
        // Set form values if settings exist
        if (data) {
          reset({
            maxSubsidyPerPerson: data.maxSubsidyPerPerson,
            maxSubsidyPerMonth: data.maxSubsidyPerMonth,
            minSpendRequired: data.minSpendRequired,
            expireDays: data.expiryRules.daysValid,
            expireEndOfMonth: data.expiryRules.expireAtEndOfMonth,
            enableEmail: data.notificationSettings.enableEmail,
            enableSMS: data.notificationSettings.enableSMS,
            enableAppNotifications: data.notificationSettings.enableAppNotifications
          });
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error("Error loading subsidy settings:", error);
        toast({
          variant: "destructive",
          title: "Failed to load settings",
          description: "Could not retrieve subsidy settings. Please try again."
        });
        setIsLoading(false);
      }
    };
    
    loadSettings();
  }, [toast, reset]);
  
  const onSaveSettings = async (data: any) => {
    if (!settings) return;
    
    setIsSaving(true);
    try {
      const updatedSettings: SubsidySettings = {
        ...settings,
        maxSubsidyPerPerson: parseFloat(data.maxSubsidyPerPerson),
        maxSubsidyPerMonth: parseFloat(data.maxSubsidyPerMonth),
        minSpendRequired: parseFloat(data.minSpendRequired),
        expiryRules: {
          daysValid: parseInt(data.expireDays),
          expireAtEndOfMonth: data.expireEndOfMonth
        },
        notificationSettings: {
          enableEmail: data.enableEmail,
          enableSMS: data.enableSMS,
          enableAppNotifications: data.enableAppNotifications
        }
      };
      
      await saveSubsidySettings(updatedSettings);
      setSettings(updatedSettings);
      
      toast({
        title: "Settings Saved",
        description: "Subsidy settings have been updated successfully."
      });
      
      setIsSaving(false);
    } catch (error) {
      console.error("Error saving subsidy settings:", error);
      toast({
        variant: "destructive",
        title: "Failed to save settings",
        description: "An error occurred while saving settings. Please try again."
      });
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Subsidy Settings</CardTitle>
          <CardDescription>
            Configure eligibility rules, spending limits, and notification preferences for subsidy programs.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Tabs defaultValue={activeTab} value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full border-b rounded-none px-6">
              <TabsTrigger value="eligibility" className="flex-1 md:flex-none">Eligibility</TabsTrigger>
              <TabsTrigger value="limits" className="flex-1 md:flex-none">Spending Limits</TabsTrigger>
              <TabsTrigger value="notifications" className="flex-1 md:flex-none">Notifications</TabsTrigger>
              <TabsTrigger value="data-import" className="flex-1 md:flex-none">Data Import</TabsTrigger>
            </TabsList>
            
            <form onSubmit={handleSubmit(onSaveSettings)}>
              <TabsContent value="eligibility" className="p-6">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium">Product Categories</h3>
                    <p className="text-muted-foreground text-sm mb-4">
                      Define which product categories are eligible for subsidy programs.
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {settings?.eligibleProductCategories.map((category) => (
                        <div key={category.categoryId} className="flex items-center space-x-2">
                          <Checkbox id={`category-${category.categoryId}`} defaultChecked />
                          <Label htmlFor={`category-${category.categoryId}`}>{category.categoryName}</Label>
                        </div>
                      ))}
                    </div>
                    
                    <div className="mt-4">
                      <Button variant="outline" className="text-sm">Add New Category</Button>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div>
                    <h3 className="text-lg font-medium">Auto-Tagging Rules</h3>
                    <p className="text-muted-foreground text-sm mb-4">
                      Configure rules to automatically tag customers based on criteria.
                    </p>
                    
                    <div className="space-y-4">
                      {settings?.autoTaggingRules.map((rule, index) => (
                        <div key={index} className="flex flex-col space-y-2 p-3 border rounded-md">
                          <div className="flex justify-between">
                            <Badge>{rule.tag}</Badge>
                            <div className="text-muted-foreground text-sm">
                              {rule.condition} {rule.value}
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      <Button variant="outline" className="text-sm">Add Tagging Rule</Button>
                    </div>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="limits" className="p-6">
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="maxSubsidyPerPerson">Maximum Subsidy Per Person (RM)</Label>
                      <Input
                        id="maxSubsidyPerPerson"
                        type="number"
                        min="0"
                        step="1"
                        {...register("maxSubsidyPerPerson", { valueAsNumber: true })}
                      />
                      <p className="text-sm text-muted-foreground">
                        Total subsidy amount a beneficiary can receive.
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="maxSubsidyPerMonth">Maximum Subsidy Per Month (RM)</Label>
                      <Input
                        id="maxSubsidyPerMonth"
                        type="number"
                        min="0"
                        step="1"
                        {...register("maxSubsidyPerMonth", { valueAsNumber: true })}
                      />
                      <p className="text-sm text-muted-foreground">
                        Monthly cap on subsidy usage per beneficiary.
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="minSpendRequired">Minimum Spend Required (RM)</Label>
                      <Input
                        id="minSpendRequired"
                        type="number"
                        min="0"
                        step="1"
                        {...register("minSpendRequired", { valueAsNumber: true })}
                      />
                      <p className="text-sm text-muted-foreground">
                        Minimum purchase amount to unlock subsidy benefit.
                      </p>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-2">
                    <h3 className="text-lg font-medium">Expiry Rules</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="expireDays">Days Until Expiry</Label>
                        <Input
                          id="expireDays"
                          type="number"
                          min="1"
                          step="1"
                          {...register("expireDays", { valueAsNumber: true })}
                        />
                      </div>
                      
                      <div className="flex items-center space-x-2 pt-8">
                        <Checkbox id="expireEndOfMonth" {...register("expireEndOfMonth")} />
                        <Label htmlFor="expireEndOfMonth">Always expire at end of month</Label>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="notifications" className="p-6">
                <div className="space-y-6">
                  <h3 className="text-lg font-medium">Notification Settings</h3>
                  <p className="text-muted-foreground">
                    Configure how beneficiaries are notified about their subsidies.
                  </p>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="enableEmail">Email Notifications</Label>
                        <p className="text-sm text-muted-foreground">
                          Send emails for subsidy allocation and expiry.
                        </p>
                      </div>
                      <Switch id="enableEmail" {...register("enableEmail")} />
                    </div>
                    
                    <Separator />
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="enableSMS">SMS Notifications</Label>
                        <p className="text-sm text-muted-foreground">
                          Send text messages for subsidy allocation and expiry.
                        </p>
                      </div>
                      <Switch id="enableSMS" {...register("enableSMS")} />
                    </div>
                    
                    <Separator />
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="enableAppNotifications">In-App Notifications</Label>
                        <p className="text-sm text-muted-foreground">
                          Show notifications in the POS application.
                        </p>
                      </div>
                      <Switch id="enableAppNotifications" {...register("enableAppNotifications")} />
                    </div>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="data-import" className="p-6">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium">Import Government Eligibility List</h3>
                    <p className="text-muted-foreground text-sm mb-4">
                      Upload official eligibility lists for automatic beneficiary registration.
                    </p>
                    
                    <div className="border-2 border-dashed rounded-md p-6 flex flex-col items-center justify-center">
                      <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                      <p className="mb-1 font-medium">Drop file or click to upload</p>
                      <p className="text-sm text-muted-foreground">
                        Supported formats: CSV, Excel (.xlsx)
                      </p>
                      <Button variant="outline" className="mt-4">
                        Select File
                      </Button>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div>
                    <h3 className="text-lg font-medium">Column Mapping</h3>
                    <p className="text-muted-foreground text-sm mb-4">
                      Map columns from your file to our system fields.
                    </p>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Name Column</Label>
                        <Input disabled placeholder="Not configured" />
                      </div>
                      <div className="space-y-2">
                        <Label>IC Number Column</Label>
                        <Input disabled placeholder="Not configured" />
                      </div>
                      <div className="space-y-2">
                        <Label>Income Column</Label>
                        <Input disabled placeholder="Not configured" />
                      </div>
                      <div className="space-y-2">
                        <Label>Address Column</Label>
                        <Input disabled placeholder="Not configured" />
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>
              
              <div className="flex justify-end gap-2 p-6">
                <Button type="button" variant="outline" disabled={isSaving}>
                  <RefreshCcw className="mr-2 h-4 w-4" />
                  Reset
                </Button>
                <Button type="submit" disabled={!isDirty || isSaving}>
                  {isSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Save Settings
                    </>
                  )}
                </Button>
              </div>
            </form>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubsidySettingsTab;
